import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useCurrency } from "@/context/CurrencyContext";
import { ShoppingBag, Truck, ShieldCheck, ArrowLeft, Minus, Plus, Heart } from "lucide-react";
import { toast } from "sonner";
import ProductReviews from "@/components/ProductReviews";
import StarRating from "@/components/StarRating";
import { resolveImageUrl } from "@/components/ImageUploader";

const ProductDetail = () => {
  const { id } = useParams();
  const { lang, t } = useLang();
  const { add } = useCart();
  const { has: isWished, toggle: toggleWish } = useWishlist();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [variantId, setVariantId] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setActiveImg(0);
      const vs = (r.data.variants || []).filter((v) => (v.stock ?? 0) > 0);
      setVariantId(vs.length ? vs[0].id : null);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
      setProduct(null);
    });
  }, [id]);

  if (loading) return <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">Loading...</div>;
  if (!product) return <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 text-center">Not found · <Link to="/shop" className="underline">Shop</Link></div>;

  const name = lang === "el" ? product.name_el : product.name_en;
  const desc = lang === "el" ? product.description_el : product.description_en;
  const variants = product.variants || [];
  const selectedVariant = variants.find((v) => v.id === variantId);
  const basePrice = product.sale_price || product.price;
  const effectivePrice = basePrice + (selectedVariant?.price_diff || 0);
  const onSale = product.sale_price && product.sale_price < product.price;
  const images = product.images || [];

  const handleAdd = () => {
    add(product, qty, variantId);
    toast.success(t("product.addToCart") + " ✓");
  };

  const handleBuyNow = () => {
    add(product, qty, variantId);
    navigate("/checkout");
  };

  return (
    <div data-testid="product-detail" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      <button onClick={() => navigate(-1)} className="text-sm flex items-center gap-2 mb-8 hover:text-[#D4AF37] transition" data-testid="back-button">
        <ArrowLeft size={16} /> Back
      </button>
      <div className="grid lg:grid-cols-2 gap-12">
        {/* Images */}
        <div className="space-y-4">
          <div className="aspect-square bg-[#F5F2ED] overflow-hidden">
            {images[activeImg] && (
              <img src={resolveImageUrl(images[activeImg])} alt={name} className="w-full h-full object-cover" />
            )}
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-4 gap-3">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square overflow-hidden border-2 ${activeImg === i ? "border-[#D4AF37]" : "border-transparent"}`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="space-y-6">
          {product.brand && <p className="overline">{product.brand}</p>}
          <h1 className="font-serif text-3xl lg:text-5xl leading-tight">{name}</h1>

          {(product.avg_rating > 0 || product.review_count > 0) && (
            <div className="flex items-center gap-3" data-testid="product-rating">
              <StarRating value={product.avg_rating} size="sm" />
              <span className="text-sm text-[#736B66]">
                {product.avg_rating > 0 ? product.avg_rating.toFixed(1) : "—"}
                {product.review_count > 0 && ` (${product.review_count})`}
              </span>
            </div>
          )}

          <div className="flex items-baseline gap-3" data-testid="product-price">
            {onSale ? (
              <>
                <span className="text-3xl font-semibold text-[#8B0000]">{format(effectivePrice)}</span>
                <span className="text-lg text-[#736B66] line-through">{format(product.price)}</span>
                <span className="bg-[#8B0000] text-white text-xs px-3 py-1 uppercase tracking-widest font-semibold">{t("product.sale")}</span>
              </>
            ) : (
              <span className="text-3xl font-semibold">{format(effectivePrice)}</span>
            )}
          </div>

          <p className="text-[#736B66] leading-relaxed">{desc}</p>

          {/* Variants */}
          {variants.length > 0 && (
            <div className="pt-4" data-testid="variant-selector">
              <p className="text-sm tracking-widest uppercase mb-3">Επιλογές</p>
              <div className="flex flex-wrap gap-2">
                {variants.map((v) => {
                  const isSelected = v.id === variantId;
                  const isOut = (v.stock ?? 0) <= 0;
                  const label = Object.entries(v.attributes || {})
                    .map(([k, val]) => val)
                    .join(" / ") || (v.sku || v.id);
                  return (
                    <button
                      key={v.id}
                      type="button"
                      disabled={isOut}
                      onClick={() => setVariantId(v.id)}
                      className={`px-4 py-2 text-sm border transition-colors ${
                        isSelected
                          ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#2C1E16] font-semibold"
                          : isOut
                            ? "border-[#EBE5DF] text-[#736B66] line-through opacity-50 cursor-not-allowed"
                            : "border-[#EBE5DF] hover:border-[#2C1E16]"
                      }`}
                      data-testid={`variant-${v.id}`}
                    >
                      {label}
                      {v.price_diff ? (
                        <span className="ml-1 text-xs text-[#D4AF37]">
                          {v.price_diff > 0 ? `+${format(v.price_diff)}` : format(v.price_diff)}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="flex items-center gap-4 pt-2">
            <p className="text-sm tracking-widest uppercase">{t("product.quantity")}</p>
            <div className="flex items-center border border-[#EBE5DF]">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 hover:bg-[#F5F2ED]" data-testid="qty-minus">
                <Minus size={14} />
              </button>
              <span className="w-12 text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-3 py-2 hover:bg-[#F5F2ED]" data-testid="qty-plus">
                <Plus size={14} />
              </button>
            </div>
            <span className="text-sm text-[#736B66]">{product.stock > 0 ? t("product.inStock") : t("product.outOfStock")}</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4">
            <button onClick={handleAdd} className="luxury-btn-outline" data-testid="add-to-cart-button">
              <ShoppingBag size={16} /> {t("product.addToCart")}
            </button>
            <button onClick={handleBuyNow} className="luxury-btn" data-testid="buy-now-button">
              {t("product.buyNow")}
            </button>
          </div>

          <button
            type="button"
            onClick={() => toggleWish(product.id)}
            className="inline-flex items-center gap-2 text-sm tracking-widest uppercase font-medium hover:text-[#8B0000] transition-colors"
            data-testid="wishlist-toggle"
          >
            <Heart size={16} fill={isWished(product.id) ? "#8B0000" : "transparent"} strokeWidth={1.8} className={isWished(product.id) ? "text-[#8B0000]" : ""} />
            {isWished(product.id) ? t("wishlist.removeFromWishlist") : t("wishlist.addToWishlist")}
          </button>

          <div className="pt-6 border-t border-[#EBE5DF] grid grid-cols-2 gap-6">
            <div className="flex items-center gap-3 text-sm">
              <Truck size={20} className="text-[#D4AF37]" />
              {t("product.free_shipping")}
            </div>
            <div className="flex items-center gap-3 text-sm">
              <ShieldCheck size={20} className="text-[#D4AF37]" />
              Secure payment
            </div>
          </div>

          <div className="pt-6 space-y-2 text-sm text-[#736B66]">
            {product.sku && <p><span className="text-[#2C1E16] font-medium">{t("product.sku")}:</span> {product.sku}</p>}
            <p><span className="text-[#2C1E16] font-medium">{t("product.category")}:</span> {product.category_slug}</p>
          </div>
        </div>
      </div>

      <ProductReviews
        productId={product.id}
        onStatsChange={(s) => setProduct((p) => p ? { ...p, ...s } : p)}
      />
    </div>
  );
};

export default ProductDetail;
